import React, { ChangeEvent, SyntheticEvent, useState } from 'react'

import AlmaWidget from './AlmaWidget'
import {
  computeOptionsCents,
  computeOptionsToParams,
  formatOptionsSummary,
} from '../../../../shared/js/options-utils'
import Dialog from '../shared/Dialog'
import { formatPrice } from '../../../../shared/js/formatters'
import { notifyCartBadge } from '../Cart'
import Option, { OptionComponentProps } from './options/Option'
import OptionsSummaryLabel from '../../../../shared/js/components/OptionsSummaryLabel'
import { put } from '../../../../shared/js/json-fetch'
import Ratings from './Ratings'
import SaleCountdown from '../shared/SaleCountdown'
import StockAlertButton from './StockAlertButton'
import Variants from './Variants'
import { RatingsProps, SaleProps, VariantsProps } from '../../shared/Module'
import { Flash } from '../../../../back/v2/js/shared/Module'

interface DescriptionProps {
  addToCartURL: string
  alma?: {
    merchantId: string
    minCents: number
    maxCents: number
  }
  authorized: boolean
  brand: {
    name: string
    url: string
  }
  cart: {
    cents: number
    quantity: number
    url: string
  }
  customerEmail?: string
  family: {
    title: string
    products: Array<{
      id: number
      name: string
      url: string
    }>
  }
  guide?: {
    content: string
    title: string
    url: string
    pdf?: string
  }
  htmlPicture: string
  id: number
  initialOptions: OptionComponentProps[]
  initialVariants: VariantsProps
  jewelleryBag: boolean
  name: string
  ratings: RatingsProps
  relatedProductsHTML?: string
  sale?: SaleProps
  shippingOfferedThreshold: number
  shortDescription: string
  stocklessSellable: boolean
}

// Display product description.
// Manage price update depending on selected options and quantity.
// Triggers add to cart: post data using fetch API.
const Description = ({
  addToCartURL,
  alma,
  authorized,
  brand,
  cart,
  customerEmail,
  guide,
  htmlPicture,
  family,
  id,
  jewelleryBag,
  name,
  initialOptions = [],
  initialVariants,
  relatedProductsHTML,
  ratings,
  sale,
  shippingOfferedThreshold,
  shortDescription,
  stocklessSellable,
}: DescriptionProps) => {
  const [cartQuantity, setCartQuantity] = useState(cart.quantity)
  const [dialog, setDialog] = useState('')
  const [pending, setPending] = useState(false)
  const [variants, setVariants] = useState(initialVariants?.variants ?? [])
  const [flash, setFlash] = useState<Flash | null>(null)
  const [options, setOptions] = useState(initialOptions)
  const [variantsAlerts, setVariantsAlerts] = useState<number[]>([])

  // Find the current selected variant.  Defaults to first entry
  const selectedVariant =
    variants.find(({ selected }) => selected) || variants[0]
  const {
    cents,
    id: selectedVariantId,
    purchasable,
    regularCents = 0,
    reference,
    shipping: { deliveryLabel },
  } = selectedVariant
  const { optionsCents, optionsTaxCents } = computeOptionsCents(options)
  const totalCents = cents + optionsCents + optionsTaxCents
  const totalRegularCents = regularCents + optionsCents + optionsTaxCents
  // Check cart actual cents, selected variant and options cents for free shipping eligibility
  const freeShipping = cart.cents + totalCents >= shippingOfferedThreshold
  const sellable = stocklessSellable || selectedVariant.quantity > 0
  const addableToCart = sellable && !pending && purchasable && authorized
  const optionsWrapperClasses =
    options.length > 1 ? 'u-mb-2@us u-mb-3@fs' : 'u-mr-2 u-mb-2'

  // Options are disabled when product is not purchasable or when customer is
  // currently adding it to the cart.
  const optionsDisabled = !addableToCart || pending
  const joinDynamicId = `pc-${id}`

  return (
    <>
      <header className='c-product-information__wrapper'>
        {/* Info Header */}
        <div className='o-order'>
          <h1 className='c-product-information__title'>{name}</h1>
          {/* Doublonnage du prix pour un affichage en sticky sur mobile */}
          <span className='c-price c-price--sticky'>
            <span className='c-price__amount'>{formatPrice(totalCents)}</span>
          </span>
          {sale && <SaleCountdown {...sale} />}
          {ratings && <Ratings {...ratings} />}
        </div>
        <div className='c-product-information__reference'>
          {reference && (
            <>
              <abbr title='Référence'>Réf.</abbr>
              {` ${reference}`}
            </>
          )}
          {brand?.name && (
            <>
              {reference ? ' - ' : ''}
              <a href={brand.url}>{brand.name}</a>
            </>
          )}
        </div>
      </header>

      {/* Price and shipping */}
      <div className='c-product-information__wrapper u-mb-2'>
        <span className='c-price u-mr-2'>
          <ins>{formatPrice(totalCents)}</ins>{' '}
          {regularCents > 0 && <del>{formatPrice(totalRegularCents)}</del>}
        </span>
        {freeShipping && (
          <strong className='c-tag c-tag--big-highlight'>
            livraison offerte
          </strong>
        )}
        {alma?.merchantId &&
          alma.minCents <= totalCents &&
          (!alma.maxCents || alma.maxCents > totalCents) && (
            <AlmaWidget merchantId={alma.merchantId} cents={totalCents} />
          )}
        <div id={`widget-${joinDynamicId}`} style={{ marginTop: '1.5rem' }} />
        {/* React will insert that script in the header */}
        <script
          type='text/javascript'
          async
          src={`https://premiercadeau.my.join-stories.com/widgets/${joinDynamicId}/index.js`}
          data-join-widget-id={`widget-${joinDynamicId}`}
          data-join-widget-alias={joinDynamicId}
          data-cookieconsent='preferences'
        />
      </div>

      {deliveryLabel && (
        <strong className='c-product-information__delivery c-tag c-tag--big-mark'>
          {deliveryLabel}
        </strong>
      )}

      {/* Description and delivery info */}
      <div className='c-product-information__wrapper u-mb-2 u-hidden@us'>
        <p
          className='u-mb-05'
          dangerouslySetInnerHTML={{ __html: shortDescription }}
        ></p>
        <a data-target='disclosure-description' href='#description'>
          Lire la suite
        </a>
      </div>

      <form onSubmit={addToCart}>
        {/* Family dropdown (if applicable); clicks redirect to related product pages */}
        {family?.products?.length > 0 && (
          <div className='u-mb-3'>
            <label htmlFor='select-family'>{family.title}</label>
            <select
              id='select-family'
              className='c-select c-select--inline'
              onChange={handleFamilyRedirect}
              value={id}
            >
              {family.products.map(({ id: prodId, name: prodName }) => (
                <option key={prodId} role='link' value={prodId}>
                  {prodName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Size guide CTA, if applicable */}
        {guide && (
          <a
            aria-haspopup='dialog'
            className='c-link c-link--icon u-mb-2'
            href={guide.url}
            onClick={openGuideDialog}
          >
            {guide.title}
          </a>
        )}

        {/* Variant selection, if applicable */}
        {variants && (
          <Variants
            {...initialVariants}
            {...variants}
            selectedId={selectedVariantId}
            onChange={setVariants}
          />
        )}
        {/* Has-a-bag info tag */}
        {jewelleryBag && (
          <div className='c-tag--container u-mb-4'>
            <mark className='c-tag c-tag--accessory'>
              Livré avec son écrin et sac à bijou
            </mark>
          </div>
        )}

        {/* Cart existing items for this product, and action buttons: option selection, add-to-cart proper */}
        {cartQuantity > 0 && (
          <div>
            <small>
              Déjà ajouté {cartQuantity > 1 ? `${cartQuantity} fois` : ''} à
              votre panier
            </small>
          </div>
        )}
        {options.map((option) => (
          <div key={option.id} className={optionsWrapperClasses}>
            <Option
              {...option}
              onCancel={handleOptionCancel}
              onChange={handleOptionChange}
              disabled={optionsDisabled}
              hidePrice
            />
          </div>
        ))}
        <AddToCartButton
          addableToCart={addableToCart}
          customerEmail={customerEmail}
          pending={pending}
          onCreatedAlert={onCreatedAlert}
          variantId={selectedVariantId}
          disabled={variantsAlerts?.includes(selectedVariantId)}
        />
        {flash && (
          <div className={`c-message c-message--${flash.kind}`}>
            {flash.message}
          </div>
        )}
      </form>

      {/* Actual size guide contents (triggered by earlier CTA) */}
      {guide && (
        <Dialog
          className='right-modal'
          onCancel={closeDialog}
          opened={dialog === 'guide'}
          title={guide.title}
          id='guide-dialog'
        >
          <div
            className='u-font-sm'
            dangerouslySetInnerHTML={{ __html: guide.content }}
          />
          <span className='u-block u-mb-2@us u-mb-3@fs'>
            <a
              className='c-link c-link--icon'
              href='/page/nos-guides'
              rel='noopener noreferrer'
            >
              Découvrez l’ensemble de nos guides ici
            </a>
          </span>
          {guide && guide.pdf && (
            <a
              className='c-btn c-btn--print'
              href={guide.pdf}
              rel='noopener noreferrer'
              target='_blank'
            >
              <svg className='c-btn__icon'>
                <use xlinkHref='#print'></use>
              </svg>
              Imprimer le guide des tailles
            </a>
          )}
        </Dialog>
      )}

      {/* Add-to-cart confirmation dialog, with related products and all */}
      <Dialog
        className='center-modal'
        onCancel={closeAddToCartDialog}
        opened={dialog === 'add-to-cart'}
        title='Cet article a été ajouté à votre panier'
      >
        <div className='c-added-to-cart'>
          <article className='o-table o-table--reverse c-added-to-cart__product'>
            <div className='o-table__cell c-added-to-cart__item'>
              <strong className='c-added-to-cart__name'>{name}</strong>
              <ins>{formatPrice(totalCents)}</ins>{' '}
              {regularCents > 0 && <del>{formatPrice(totalRegularCents)}</del>}
              {computeSummary()}
            </div>
            {htmlPicture && (
              <div
                className='o-table__cell c-added-to-cart__thumbnail'
                dangerouslySetInnerHTML={{ __html: htmlPicture }}
              />
            )}
          </article>
          <div className='c-added-to-cart__action'>
            {/* TODO: gérer l'option papier cadeau */}
            {/* <div className='c-added-to-cart__gift'>
              <input id='gift' type='checkbox' name='gift' />
              <label
                className='c-label c-label--checkbox c-label--checkbox c-label--highlight'
                htmlFor='gift'
              >
                option papier cadeau (+ 2€)
              </label>
            </div> */}
            <a
              href='#'
              className='c-btn c-btn--back'
              onClick={closeAddToCartDialog}
            >
              <svg className='c-btn__icon'>
                <use xlinkHref='#arrow'></use>
              </svg>
              Continuer mes achats
            </a>
            <a href={cart.url} className='c-btn c-btn--primary'>
              Voir mon panier
            </a>
          </div>
        </div>
        {relatedProductsHTML && (
          <div dangerouslySetInnerHTML={{ __html: relatedProductsHTML }} />
        )}
      </Dialog>
    </>
  )

  async function addToCart(event: SyntheticEvent) {
    event?.preventDefault()
    setPending(true)

    // Post update to the server. Expected params must look like this:
    // line_item[product_options][selection][*optionId*]: *optionValue (id or text)*
    const { error, flash } = await put(addToCartURL, {
      property_set_id: selectedVariantId,
      line_item: {
        product_options: { selection: computeOptionsToParams(options) },
      },
    })
    if (error) {
      setFlash(flash)
      return
    }

    const size = cartQuantity + 1
    setCartQuantity(size)
    notifyCartBadge(size)
    setPending(false)
    setDialog('add-to-cart')
  }

  // Massage selected options to get a user readable summary
  function computeSummary() {
    const labels = formatOptionsSummary(options)
    if (labels.length > 0) {
      return labels.map(([title, label], index) => (
        <div key={`${title}-${index}`}>
          <OptionsSummaryLabel title={title} label={label} />
          &nbsp;-&nbsp;
          {formatPrice(optionsCents + optionsTaxCents)}
        </div>
      ))
    }
  }

  function closeAddToCartDialog(event: SyntheticEvent) {
    closeDialog(event)
    setOptions(initialOptions)
  }

  function closeDialog(event: SyntheticEvent) {
    event?.preventDefault()
    setDialog('')
  }

  function handleFamilyRedirect({
    target: { value },
  }: ChangeEvent<HTMLSelectElement>) {
    const productId = Number(value)
    const product = family.products.find((product) => product.id === productId)
    const { url } = product || {}
    // Do not redirect current product to itself
    if (url && id !== productId) {
      window.location.href = url
    }
  }

  // Reset a given option to its initial value
  function handleOptionCancel(optId: number) {
    const values = initialOptions?.find((option) => option.id === optId)?.values
    if (!values) return

    const updatedOptions = options.map((option) =>
      option.id === optId ? { ...option, values } : option
    )
    setOptions(updatedOptions)
  }

  // Dynamically store filled values for expected option
  function handleOptionChange(optId: number, values: any) {
    const updatedOptions = options.map((option) =>
      option.id === optId ? { ...option, values } : option
    )
    setOptions(updatedOptions)
  }

  function openGuideDialog(event: SyntheticEvent) {
    event?.preventDefault()
    setDialog('guide')
  }

  function onCreatedAlert(variantId: number) {
    setVariantsAlerts([...variantsAlerts, variantId])
  }
}

export default Description

// --------------------------------------------------------------------

interface AddToCartButtonProps {
  addableToCart: boolean
  customerEmail?: string
  disabled: boolean
  pending: boolean
  onCreatedAlert: Function
  variantId: number
}

function AddToCartButton({
  addableToCart,
  customerEmail,
  disabled,
  pending,
  onCreatedAlert,
  variantId,
}: AddToCartButtonProps) {
  if (addableToCart) {
    return (
      <button
        className='c-btn c-btn--primary c-btn--sticky-cart u-mb-2'
        type='submit'
      >
        Ajouter au panier
      </button>
    )
  }

  if (pending) {
    return (
      <button
        className='c-btn c-btn--primary c-btn--sticky-cart u-mb-2'
        disabled
      >
        Ajout en cours…
      </button>
    )
  }

  return (
    <StockAlertButton
      disabled={disabled}
      customerEmail={customerEmail}
      onCreatedAlert={onCreatedAlert}
      variantId={variantId}
    />
  )
}
